import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DecorationTypeEnum, DesignToolCanvas, DesignToolPosition, Designs, PositionDesign, VariantColors, VariantImages } from '@core/models/designTool';
import { Store, select } from '@ngrx/store';
import * as CatalogActions from '@core/state/catalog/catalog.actions';
import * as DesignToolActions from '@core/state/design-tool/design-tool.actions'
import * as fromCatalog from '@core/state/catalog/catalog.selectors';
import { Client } from '@core/models/client';
import { SessionService } from '@core/services/session/session.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { EMPTY, Observable, Subject, combineLatest, of, switchMap, take, takeUntil, finalize, filter } from 'rxjs';
import { MongoDbService } from '@core/services/api/mongodb.service';
import { HelperService } from '@core/services/api/helper.service';
import * as fromDesignTool from '@core/state/design-tool/design-tool.selectors';
import { AddToCartModalComponent } from './add-to-cart-modal/add-to-cart-modal.component';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import { CanvasService } from '@core/services/fabric/canvas.service';
import { GlobalLoaderService } from '@core/services/global-loader.service';
import { Cart, CartDesign, CartPosition, CartProduct, MockupsUploadResponse } from '@core/models/catalog';
import { selectDesignToolMockups } from '@core/state/design-tool/design-tool.selectors';
import ObjectId from 'bson-objectid';
import { SharedService } from '@shared/shared.service';
import { DataLayerService } from '@core/services/seo/data-layer.service';

@Component({
  selector: 'product-design-options',
  templateUrl: './product-design-options.component.html',
  styleUrls: ['./product-design-options.component.scss']
})
export class ProductDesignOptionsComponent implements OnInit, OnDestroy {
  client: Client;
  @Input() sizes: any[];
  @Input() position: string;
  @Input() product: { brand: string, style: string, color: string };
  @Input() carts: any;
  @Input() designName: string;
  @Input() designObject: Designs;
  @Input() designId: string;
  @Input() cartId: string;
  @Input() variant: VariantColors;
  @Input() decorationType: DecorationTypeEnum;
  canvasDimensions$: Observable<DesignToolCanvas> = this.store.select(fromDesignTool.selectCanvasDimensions);
  canvasDimensions: DesignToolCanvas;
  @Output() selectedPosition = new EventEmitter<string>();
  @Output() priceChangesEvent = new EventEmitter<string>();
  productTotals: number;
  designImage: string;
  productSizes$: Observable<VariantColors[]> = this.store.select(fromCatalog.selectProductVariantColor)
  productSizes: VariantColors[];
  updatedProductSizes$: Observable<VariantColors[]> = this.store.select(fromCatalog.selectPricingProductVariantColor);
  activePosition$: Observable<string> = this.store.select(fromDesignTool.selectCurrentPosition);
  designPositions$: Observable<Partial<DesignToolPosition>[]> = this.store.select(fromDesignTool.selectDesignToolPositions);
  designPositions: Partial<DesignToolPosition>[] = [];
  mobileView$ = this.store.select(fromDesignTool.selectMobileView)
  mobileView: boolean;
  updatedProductSizes: VariantColors[];
  selectedItemIndex: number = -1; // Index of the currently selected accordion item
  cartName: string = '';
  newDesignId: string = '';
  sizeQtys: number[];
  vendorStyleId: number;
  cartToBeEdited: Cart;
  exisitingSizeQtys: string[];
  editingProduct: boolean
  color: string;
  brand: string;
  style: string;
  destroy$ = new Subject<void>();
  vendorName: string;

  constructor(
    private store: Store,
    protected router: Router,
    private route: ActivatedRoute,
    private _mongo: MongoDbService,
    private helper: HelperService,
    private canvasService: CanvasService,
    private sessionService: SessionService,
    private sharedService: SharedService,
    private modalService: MdbModalService,
    private dataLayerService: DataLayerService,
    private globalLoaderService: GlobalLoaderService
  ) {
    this.client = this.sessionService.getActiveClient();
  }

  ngOnInit(): void {    
    this.mobileView$.pipe(takeUntil(this.destroy$)).subscribe(mobileView => this.mobileView = mobileView)
    this.updatedProductSizes$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if(data) {
        this.updatedProductSizes = this.getUniqueSizesAndSort(data)
        if(this.updatedProductSizes.length > 0){
          this.productSizes = this.getUniqueSizesAndSort(this.updatedProductSizes);
        }
      }
    })
    this.productSizes$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if(data){
        this.vendorStyleId = data?.[0]?.vendorStyleId;
        this.vendorName = data?.[0]?.vendorName;
        this.productSizes = this.getUniqueSizesAndSort(data);
      }
    })

    this.activePosition$.pipe(takeUntil(this.destroy$)).subscribe(activePosition => {
      this.position = activePosition
    })

    this.canvasDimensions$.pipe(takeUntil(this.destroy$)).subscribe((canvasDimensions: DesignToolCanvas) => {
      this.canvasDimensions = canvasDimensions
    })

    this.store.pipe(select(fromDesignTool.selectImageDesign), takeUntil(this.destroy$)).subscribe((designImg => {
      this.designImage = designImg
    }));
    this.store.pipe(select(fromCatalog.selectCarts), takeUntil(this.destroy$)).subscribe((carts => {
      this.carts = carts
    }));

    this.route.queryParams.subscribe((params: Params) => {
      if(params?.color && params?.design) {
        if (params?.cart) {
          this.editingProduct = true
        }
        this.color = params.color;
        this.brand = this.route.snapshot.params.brand;
        this.style = this.route.snapshot.params.style;

        combineLatest([this.store.select(fromCatalog.selectVariantQtysFromCartById(params.cart, params.design)), this.productSizes$])
        .pipe(filter(([qtys, productSizes]) => productSizes?.length > 0), take(1))
        .subscribe(([qtys, productSizes]) => {
          if (!this.sizeQtys) {
            this.exisitingSizeQtys = qtys
            this.sizeQtys = this.exisitingSizeQtys?.length > 0 ? this.exisitingSizeQtys : new Array(productSizes.length).fill(0);
            setTimeout(() => {
              this.updatePricing();
            }, 4000)
          }
        });
        if (!this.cartToBeEdited) {
          this.store.pipe(select(fromCatalog.selectCartById(this.cartId)), take(1)).subscribe((cart => {
            this.cartToBeEdited = cart
          }));
        }
      }
    })

    combineLatest([this.store.select(selectDesignToolMockups), this.designPositions$])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([designMockups, designPositions]) => {
      // filter null values
      this.designPositions = !!designMockups ? 
      Object.entries(designMockups)
      ?.filter((e: ['front'|'side'|'back', string]) => !!e[1])
      .map((designMockupEntry: ['front'|'side'|'back', string]) => {
        const positionInDesignObject = designPositions?.find(e => e.position == designMockupEntry[0])
        const newE = {
          ...positionInDesignObject,
          position: designMockupEntry[0],
          url: designMockupEntry[1],
        }
        return newE
      }) : []
      if (this.designPositions?.length > 0) {
        this.updatePricing()
      }
    })
  }

  updatePricing() {
    this.productTotals = this.calculateQtyBeforeSubmit(this.sizeQtys);
          //check if quantities are entered, if not default 1
      const positions = this.designPositions?.map(d => {
        const positionData = {
          position: d.position,
          decorationType: d.decorationType ?? this.decorationType ?? DecorationTypeEnum.ScreenPrint,
          canvas: []
        };
        if (d.canvas) {
          positionData.canvas = d.canvas.map((image, index) => {
            const stitchesPerSquareInch = 100
            const pxWidth = (image.width * image.scaleX)
            const pxHeight = (image.height * image.scaleY)
            const heightInInches = (pxHeight * this.canvasDimensions.printingCanvas[d.position].heightInches) / this.canvasDimensions.printingCanvas[d.position].height
            const widthInInches = (pxWidth * this.canvasDimensions.printingCanvas[d.position].widthInches) / this.canvasDimensions.printingCanvas[d.position].width
            const areaInInches = widthInInches * heightInInches
            const stitchCount = areaInInches * stitchesPerSquareInch
            
            const newImage = {
              colorCount: image.colorCount,
              stitchCount,
              widthInInches,
              heightInInches,
              areaInInches,
              stitchesPerSquareInch,
              decorationType: positionData.decorationType,
              // decorationType: image.decorationType, // decoration type by image in canvas
            }
            return newImage
          });
        }
        return positionData;
      }) ?? [];
      this.store.dispatch(CatalogActions.updatePricingProductVariantColor({
        brand: this.brand,
        style: this.style,
        color: this.color,
        clientId: this.client.id,
        positions,
        qty: this.productTotals
      }));
  }


  calculateQtyBeforeSubmit(quantities?: number[]): number {
    return quantities?.reduce((acc, current) => acc + current, 0) ?? 0;
  }

  getFileName(url) {
    // get t.positionImagesArrayhe filename from the file path with the file extension
    const filename = url.split('/').pop();
    return filename
  }

  getUniqueSizesAndSort(data) {
    let sizeData = [];
    const addedSizes = new Set();

    data.forEach(item => {
      if (!addedSizes.has(item.size)) {
        sizeData.push({ size: item.size, price: item.price });
        addedSizes.add(item.size);
      }
    });

    // Step 2: Sort the array based on sizes
    sizeData.sort((a, b) => this.sizeSort(a.size, b.size));

    return sizeData;

  }

  sizeSort = (a, b) => {

    const sizeOrder = ["XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];

    const indexA = sizeOrder.indexOf(a);
    const indexB = sizeOrder.indexOf(b);

    // If both sizes are in the custom order, compare their indices
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If one size is in the custom order and the other is not, prioritize the one in the custom order
    if (indexA !== -1) {
      return -1;
    }
    if (indexB !== -1) {
      return 1;
    }

    // If neither size is in the custom order, compare them alphabetically
    return a.localeCompare(b);
  };

  onPositionSelect(position: string) {
    this.selectedPosition.emit(position);
  }

  lockImage(image: PositionDesign) {
    this.canvasService.lockImagePosition(image.url);
  }

  editImage(image: PositionDesign) {
    this.canvasService.editImagePosition(image.url);
  }

  deleteImage(image: PositionDesign) {
    this.canvasService.deleteObject(image.url)
  }

  capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  checkIfImagesExist(positionImageArray) {
    return positionImageArray.filter(item => item.canvas && item.canvas.length > 0);
  }

  toggleAccordion(index: number): void {
    if (this.selectedItemIndex === index) {
      this.selectedItemIndex = -1; // Collapse if already open
    } else {
      this.selectedItemIndex = index; // Expand the selected item
    }
  }

  toggleNewCartInput(event: Event) {
    event.stopPropagation(); // Prevent the click event from propagating
    this.cartName = ''; // Clear input field when toggling
  }
  /**
   * Asynchronous function to add a new cart.
   */
  async addNewCart() {
    const cart = await this._mongo.cartTemplate()

    if (this.cartName) {
      cart.cartName = this.cartName;
      this.toggleNewCartInput(event); // Hide input field after adding
    } else {
      this.openAlert('Please input a Cart name.');
      return
    }

    this.store.dispatch(CatalogActions.addCart({
      cart: cart
    }));

    this.store.pipe(select(fromCatalog.selectCarts), take(1)).subscribe((carts => {
      this.carts = carts
    }));
  }

  openAlert(prompt) {
    this.sharedService.showNotification({ data: { title: prompt, class: '' }, autohide: true });

  }

  openAlertSuccess(prompt) {
    this.sharedService.showNotification({ data: { title: prompt, class: 'bg-success', autohide: true } });
  }

  addDesignToCart(cart: Cart, edit = false) {
    this.globalLoaderService.setMessage(`${edit ? 'Updating' : 'Adding'} your design ${edit ? 'in' : 'to'} cart ${cart.cartName}...`)
    this.globalLoaderService.show()
    // Use switchMap to handle the asynchronous nature of observables
    combineLatest([
      this.store.select(fromDesignTool.selectDesignObject),
      this.store.select(fromDesignTool.selectDesignToolMockups)
    ]).pipe(
      take(1),
      switchMap(async ([design, mockups]: [Designs, VariantImages]) => {
        if (design?._id && mockups) {
          const sizeQtys = [...this.sizeQtys]
          this.newDesignId = design._id;

          // Only create S3 image links if positions exist
          if(design.positions.length > 0) {
          const positionUrls: MockupsUploadResponse[] = await this.canvasService.exportCanvasAsImageS3(design, this.canvasDimensions, this.variant, mockups);
          this.store.dispatch(DesignToolActions.updateDesignPositionCanvasImage({ updates: positionUrls }));

          this.designPositions.forEach((designPosition, index) => {
            designPosition.url = positionUrls?.[index]?.url ?? ''
          })
        }

          
          const positionData: CartPosition[] = this.designPositionToCartPosition(this.designPositions);
          const productData: Partial<CartProduct> = this.getCartProduct(sizeQtys);
          let updateDesigns: Partial<CartDesign>[];
          const newDesign: Partial<CartDesign> = {
            _id: this.newDesignId,
            designName: design.name,
            image: this.designImage,
            positions: positionData,
            products: productData,
          };
          if (!edit) {
            const existentDesign = cart.designs.find(e => e._id == design._id)
            if (existentDesign) {
              newDesign._id = ObjectId().toHexString()
            }
            updateDesigns = [...cart.designs, newDesign];
          } else {
            const allExisitingDesignsInCart = [...cart.designs];
            updateDesigns = this.replaceExistentDesignInDesignList(design._id, allExisitingDesignsInCart, newDesign)
          }

          const updateCart: Cart = {
            ...cart,
            _id: undefined,
            version: this.helper.getTimestamp(),
            designs: updateDesigns
          };
          this.dataLayerService.logAddToCart(updateCart);
          this.store.dispatch(CatalogActions.addCart({ cart: updateCart }));
          this.openAlertSuccess(`Your design has been ${edit ? 'edited in' : 'added to'} ${cart.cartName}`);
          this.router.navigate([`/cart/${cart.cartId}`]);
          return EMPTY; // or any observable that doesn't emit values
        } else {
          return of(null); // or any observable that emits a default value
        }
      }), finalize(() => this.globalLoaderService.hide())
    ).subscribe();
  }


  /**
 * Function to delete a cart item.
 *
 * @param {Object} cart - The cart object to be deleted.
 */
  deleteCart(cart) {
    if (confirm(`Are you sure you want to delete ${cart.cartName}? This will remove the cart permanently.`)) {
      const deleteCart = { ...cart, _id: undefined, version: this.helper.getTimestamp(), active: false };

      this.store.dispatch(CatalogActions.addCart({
        cart: deleteCart
      }));

      this.store.pipe(select(fromCatalog.selectCarts), take(1)).subscribe((carts => {
        this.carts = carts
      }));
    }
  }

  designPositionToCartPosition(positions: Partial<DesignToolPosition>[]): CartPosition[] {
    const cartPositions: CartPosition[] = [];
    
    positions.forEach((position, i) => {
      const positionName = position.position;
      // const placementType = position.canvas && position.canvas.length > 0 ? position.canvas[0].decorationType : "Screen Print"; // Assuming a default value
      position.canvas?.forEach?.((canvasItem) => {
        const stitchesPerSquareInch = 100
        const pxWidth = (canvasItem.width * canvasItem.scaleX)
        const pxHeight = (canvasItem.height * canvasItem.scaleY)
        const heightInInches = (pxHeight * this.canvasDimensions.printingCanvas[positionName].heightInches) / this.canvasDimensions.printingCanvas[positionName].height
        const widthInInches = (pxWidth * this.canvasDimensions.printingCanvas[positionName].widthInches) / this.canvasDimensions.printingCanvas[positionName].width
        const areaInInches = widthInInches * heightInInches
        const stitchCount = areaInInches * stitchesPerSquareInch
        // Check if an item with the same position exists in the resultArray
        const existingItemIndex = cartPositions.findIndex((resultItem) => resultItem.position === positionName);

        if (existingItemIndex !== -1) {
          // If an item with the same position exists, update its colorCount
          cartPositions[existingItemIndex].image = position.url || '';
          cartPositions[existingItemIndex].colorCount += canvasItem.colorCount || 0;
          cartPositions[existingItemIndex].stitchCount += canvasItem.stitchCount || 0;
        } else {
          let decorationType = DecorationTypeEnum.ScreenPrint
          switch(position.decorationType) {
            case DecorationTypeEnum.Print: decorationType = DecorationTypeEnum.ScreenPrint; break;
            default: decorationType = position.decorationType
          }
          // If not, push a new item to the resultArray
          cartPositions.push({
            position: positionName,
            decorationType,
            placementType: decorationType,//canvasItem.decorationType || placementType
            image: position.url || '',
            mockupImage: position.mockupUrl || '',
            colorCount: canvasItem.colorCount || 0,
            stitchCount: stitchCount || 0,
            qty: this.sizeQtys.reduce((total, num) => total + num, 0),
            notes: position.notes
          } as CartPosition);
        }
      });
    });
    return cartPositions;
  }

  updatedFormatSizeQtyToVariants(sizeQtys: number[]) {
    return this.updatedProductSizes.map((item, i) => ({
      ...item,
      color: this.color,
      qty: sizeQtys[i] ?? 0, // Convert the string to a number
      size: item.size,
      price: item.price,
    }));
  }

  getProductTitle() {
    return this.sizes[0].title || ''
  }

  getProductImages(): string[] { // this will need to refactored to get Design mocks later
    const productImages: string[] = Object.values(this.sizes[0].images)

    productImages.forEach((option, index, array) => {
      if (typeof option === 'string' && option.includes('ssactivewear')) {
        array[index] = option.replace('/Images/Style', ''); // Remove "/Images/Style"
      }
    });

    return productImages
  }

  getCartProduct(quantities: number[]): Partial<CartProduct> {
    const productData = {
      title: this.getProductTitle(),
      brand: this.brand,
      style: this.style,
      vendorStyleId: this.vendorStyleId,
      vendorName: this.vendorName,
      decoratedImages: this.getProductImages(),
      variants: this.updatedFormatSizeQtyToVariants(quantities)
    } as Partial<CartProduct>

    return productData
  }

 async openAddToCartModal() {
    const artExists = await this.checkIfImagesExist(this.designPositions)
    const validatedQtys = this.checkSizeQuantities(this.sizeQtys) // ensure size inputs are not 0

    // Check is art exists, if not then we classify the design as a Sample
    if(artExists && artExists.length == 0) {
      //If Sample, then check if quanities do not exceed 5. if so, then throw alert
      if(validatedQtys > 5 && validatedQtys < 0) {
        this.openAlert('Ordering samples is limited to 5. Please adjust quantities or add art.');
        return;
      }
    }

    if (this.sizeQtys.length > 0 && validatedQtys !== 0 && validatedQtys) {
      this.modalService.open(AddToCartModalComponent, {
        modalClass: 'modal-dialog-centered',
        ignoreBackdropClick: true, // Prevent closing on clicking outside
      }).onClose.subscribe((cart: Cart) => {
        if (cart) {
          this.addDesignToCart(cart)
        }
      });
    } else {
      this.openAlert('Please add Quantities to continue.');
    }
  }

  replaceExistentDesignInDesignList(designId: string, designsArray: Partial<CartDesign>[], newDesign: Partial<CartDesign>): Partial<CartDesign>[]|null {
    const foundIndex = designsArray.findIndex(designObject => designObject._id === designId);

    if (foundIndex !== -1) {
      designsArray[foundIndex] = newDesign;
      return designsArray;
    }
  
    return null; // Return null if the design is not found
  }

  checkSizeQuantities(arr: (number | string)[]): number {
    let sum = 0;

    for (const num of arr) {
      if (typeof num === 'number' && Number.isInteger(num)) {
        sum += num;
      } else if (typeof num === 'string' && num.trim() !== '') {
        const parsedNum = parseInt(num);
        if (!isNaN(parsedNum)) {
          sum += parsedNum;
        } else {
          console.warn(`Ignoring invalid string: ${num}`);
        }
      } else {
        console.warn(`Ignoring non-numeric value: ${num}`);
      }
    }

    return sum;
  }

  trackByFn(index: number, item: any): any {
    return item.size; // return input that was last focused
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackPositions(item?: any) {
    return item?.position
  }

  trackImages(image?: PositionDesign) {
    return image?.url
  }

  onNotesChange() {
    const positionsCopy = [...this.designPositions.map(e => ({...e}))]
    this.store.dispatch(DesignToolActions.updateDesignPositions({ designId: this.designId, positions: positionsCopy }));
  }
}
